import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import SEO from "../components/SEO";

const Leadership = () => {
  const leadershipTeam = [
    {
      name: "Irina Abdrabboh",
      title: "Founder & Chief Executive Officer",
      role: "Founder & CEO",
      image: "/images/irina.jpeg",
      description:
        "Service-Disabled Combat Veteran with executive leadership overseeing strategy and federal partnerships.",
      paragraph1:
        "Irina Abdrabboh is the Founder and Chief Executive Officer of Providence Federal Group, an SDVOSB government contracting firm operating at the intersection of federal procurement and high-stakes commercial relationships. She is an MBA graduate and a third-year Juris Doctor candidate in the Class of 2026, with a focus on construction law, government contracting, and business litigation, bringing a disciplined legal mindset to contract strategy and dispute avoidance.",
      refugeeStory:
        "Raised in a refugee and immigrant family, Irina developed resilience and judgment that continue to define her leadership style. Her path reflects a deep commitment to service, discipline, and mission-driven execution.",
      paragraph2:
        "She served in the Rhode Island Army National Guard for eight years as a Unit Armorer and Supply Specialist and completed a year-long combat deployment to the Middle East in support of Operation Enduring Freedom in 2017. Her leadership background combines military discipline with entrepreneurial and legal experience.",
      paragraph3:
        "Her legal experience includes complex business litigation and commercial dispute work at a private law firm, as well as judicial work with the U.S. District Court for the District of Rhode Island and the U.S. Court of Appeals for the First Circuit. She has also assisted veterans with VA disability claims and benefits navigation, co-owned and operated a retail business, provided strategic consulting to Boston-area startups, and developed practical insight into commercial insurance, real estate, and risk allocation.",
      callout:
        "Recognized by Most Influential Women in 2026 and selected as a Rhode Island Bar Foundation Scholar in 2023 for academic excellence, leadership, and civic impact.",
      expertise: [
        "Government Contracting",
        "Business Litigation",
        "Construction Law",
        "Risk Management",
      ],
      badge: {
        show: true,
        image:
          "https://bestagent-new-database.s3.us-east-2.amazonaws.com/configurations/config_68454009b3957.png",
        title: "Most Influential Women 2026",
        link: "https://influentialwomen.com/",
        profileLink: "https://influentialwomen.com/profile/irinaa-abraddoh",
      },
    },
    {
      name: "Omar Elouizi",
      title: "Co-Founder & Chief Operating Officer",
      role: "Co-Founder & COO",
      image: "/images/omar.jpeg",
      description:
        "Leads operational coordination and supports contract execution and performance delivery.",
      paragraph1:
        "Omar Elouizi is the Co-Founder and Chief Operating Officer of Providence Federal Group, providing operational leadership and execution oversight in support of government and regulated customers. He brings extensive experience building compliant, scalable organizations aligned with Federal Acquisition Regulation principles, performance accountability, and audit readiness.",
      paragraph2:
        "He has built, scaled, and successfully exited two retail businesses through disciplined execution and regulatory compliance. As an owner-operator, he founded and grew a business from inception to nearly $1 million in annual revenue, with full responsibility for IRS compliance, licensing, inspections, workforce management, payroll, and financial controls. He also managed multi-location retail operations generating annual revenues exceeding $3 million.",
      paragraph3:
        "Omar also held regional sales and operational leadership roles within multinational pharmaceutical organizations, where he led product launches, managed multi-state portfolios, and directed cross-functional teams in highly regulated healthcare environments. His approach is structured, compliance-driven, and execution-focused, supporting Providence Federal Group’s ability to deliver reliable, accountable, and scalable solutions aligned with federal contracting requirements.",
      callout:
        "Brings a disciplined operating approach grounded in compliance, execution, scalability, and measurable performance.",
      expertise: [
        "Operations Management",
        "Federal Compliance (FAR)",
        "Business Scaling",
        "Regulatory Strategy",
        "Portfolio Management",
      ],
      badge: {
        show: false,
      },
    },
  ];

  return (
    <>
      <SEO
        title="Leadership Team | Providence Federal Group"
        description="Meet the experienced leadership team of Providence Federal Group - a SDVOSB specializing in federal government contracting."
        keywords="SDVOSB leadership, executive team, government contracting, Rhode Island business"
      />

      <div className="min-h-screen bg-gray-50">
        <Header />

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-[#1E3A8A] via-[#2C3E50] to-[#0F1A3A] text-white pt-40 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-black/10"></div>

          <div className="max-w-7xl mx-auto px-4 relative z-10 mt-20">
            <div className="text-center max-w-4xl mx-auto">
              <h1
                className="font-display text-5xl md:text-6xl font-medium mb-6 leading-[1.1]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Executive Leadership Team
              </h1>

              <p className="text-lg md:text-2xl mb-8 leading-relaxed text-white/90">
                Meet the experienced professionals who lead Providence Federal
                Group. Our leadership brings diverse expertise in federal
                contracting, operations, and strategic growth.
              </p>

              <div className="inline-flex items-center justify-center gap-3 text-white/85">
                <span className="w-2.5 h-2.5 bg-[#C5A047] rounded-full"></span>
                <span className="text-sm md:text-base">
                  Driving excellence in federal contracting
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Leadership Team Section */}
        <section className="py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-14">
              <h2
                className="font-display text-4xl md:text-5xl font-medium text-[#2C3E50] mb-4"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Our Executive Leadership
              </h2>

              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                Combining legal expertise, operational excellence, and deep
                government contracting knowledge to drive our mission forward.
              </p>
            </div>

            <div className="space-y-10 md:space-y-12 mb-20">
              {leadershipTeam.map((leader, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300"
                >
                  <div className="p-8 md:p-10">
                    <div className="flex flex-col lg:flex-row gap-8">
                      {/* Sidebar */}
                      <div className="lg:w-[280px] xl:w-[320px] flex-shrink-0">
                        <div className="w-full max-w-xs mx-auto lg:mx-0">
                          <div className="rounded-2xl overflow-hidden shadow-md aspect-square relative group bg-gray-100">
                            <img
                              src={leader.image}
                              alt={leader.name}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src =
                                  "https://via.placeholder.com/400x400/2C3E50/FFFFFF?text=" +
                                  leader.name.split(" ")[0].charAt(0);
                              }}
                            />
                          </div>

                          <div className="mt-4 text-center lg:text-left">
                            <span className="inline-block px-4 py-2 bg-[#1E3A8A]/10 text-[#1E3A8A] text-sm font-semibold rounded-full">
                              {leader.role}
                            </span>
                          </div>

                          {leader.badge && leader.badge.show && (
                            <div className="mt-6 p-5 bg-white rounded-2xl border border-gray-200 shadow-sm">
                              <div className="flex flex-col items-center text-center">
                                <a
                                  href={leader.badge.link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="block mb-3 hover:opacity-90 transition-opacity"
                                >
                                  <img
                                    src={leader.badge.image}
                                    alt={leader.badge.title}
                                    className="w-24 h-24 object-contain mx-auto"
                                  />
                                </a>

                                <div className="text-xs font-semibold uppercase tracking-wide text-[#C5A047] mb-1">
                                  Featured By
                                </div>

                                <div className="text-sm font-semibold text-[#2C3E50] mb-2">
                                  {leader.badge.title}
                                </div>

                                <a
                                  href={leader.badge.profileLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center text-sm text-[#1E3A8A] hover:text-[#2C3E50] font-medium"
                                >
                                  View Profile
                                  <svg
                                    className="w-4 h-4 ml-1"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth="2"
                                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                                    />
                                  </svg>
                                </a>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Main content */}
                      <div className="flex-1">
                        <h3
                          className="font-display text-3xl md:text-4xl font-medium text-[#2C3E50] mb-2"
                          style={{ fontFamily: "'Cormorant Garamond', serif" }}
                        >
                          {leader.name}
                        </h3>

                        <p className="text-lg text-gray-600 mb-4">
                          {leader.title}
                        </p>

                        <p className="text-lg font-medium text-[#2C3E50] mb-4 leading-relaxed">
                          {leader.description}
                        </p>

                        <div className="w-12 h-[2px] bg-[#C5A047] mb-6"></div>

                        <div className="flex flex-wrap gap-2 mb-8">
                          {leader.expertise.map((skill, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1.5 bg-[#C5A047]/10 text-[#C5A047] text-sm font-medium rounded-full border border-[#C5A047]/20"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>

                        <div className="space-y-5">
                          <p className="text-gray-700 leading-relaxed">
                            {leader.paragraph1}
                          </p>

                          {leader.refugeeStory && (
                            <div className="mt-6 p-5 bg-[#F8FAFC] border-l-4 border-[#C5A047] rounded-lg">
                              <p className="text-gray-700 leading-relaxed italic">
                                {leader.refugeeStory}
                              </p>
                            </div>
                          )}

                          <p className="text-gray-700 leading-relaxed">
                            {leader.paragraph2}
                          </p>

                          <p className="text-gray-700 leading-relaxed">
                            {leader.paragraph3}
                          </p>

                          {leader.callout && (
                            <div className="mt-6 p-5 bg-[#F8FAFC] border border-gray-200 rounded-2xl">
                              <div className="flex items-start gap-3">
                                <svg
                                  className="w-5 h-5 text-[#C5A047] mt-0.5 flex-shrink-0"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                                  />
                                </svg>

                                <p className="text-gray-700 leading-relaxed">
                                  {leader.callout}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-[#1E3A8A] to-[#2C3E50] rounded-2xl p-8 md:p-12 text-white text-center shadow-xl">
              <div className="max-w-3xl mx-auto">
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-1 bg-[#C5A047]"></div>
                </div>

                <h3
                  className="font-display text-3xl md:text-4xl font-medium mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Want to Connect with Our Leadership?
                </h3>

                <p className="text-lg md:text-xl mb-8 text-white/90 leading-relaxed">
                  Interested in partnership opportunities or have questions
                  about our federal contracting capabilities?
                </p>

                <div className="flex flex-col sm:flex-row justify-center gap-4">
                  <Link
                    to="/contact"
                    className="px-8 py-4 bg-[#C5A047] text-white font-semibold rounded-full hover:bg-[#D4AF37] transition-all shadow-lg inline-flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                    Contact Us
                  </Link>

                  <a
                    href="tel:14013469778"
                    className="px-8 py-4 bg-white/10 text-white font-semibold rounded-full hover:bg-white/20 transition-all border border-white/20 inline-flex items-center justify-center"
                  >
                    <svg
                      className="w-5 h-5 mr-2"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M3 5.5C3 4.12 4.12 3 5.5 3h2.2a2 2 0 011.94 1.515l.516 2.066a2 2 0 01-.45 1.948l-1.27 1.27a16 16 0 006.586 6.586l1.27-1.27a2 2 0 011.948-.45l2.066.516A2 2 0 0121 16.72V19a2 2 0 01-2 2h-1C10.716 21 3 13.284 3 6V5.5z"
                      />
                    </svg>
                    Call Us
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Leadership;